import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useLabeling } from '../../context/useLabeling';
import type { ToolImage } from '../../types/LabelingTypes';

interface SeriesGroup {
  toolId: string;
  items: ToolImage[];
}

function buildGroups(items: ToolImage[]): SeriesGroup[] {
  const map = new Map<string, ToolImage[]>();
  for (const item of items) {
    const group = map.get(item.toolId) ?? [];
    group.push(item);
    map.set(item.toolId, group);
  }
  return Array.from(map.entries()).map(([toolId, groupItems]) => ({ toolId, items: groupItems }));
}

interface ItemRowProps {
  item: ToolImage;
  isActive: boolean;
  onClick: () => void;
}

function ItemRow({ item, isActive, onClick }: ItemRowProps) {
  const dotColor =
    item.label === 'ok'
      ? 'bg-ok'
      : item.label === 'worn'
        ? 'bg-worn'
        : item.label === 'unreadable'
          ? 'bg-unreadable'
          : 'border border-border';

  return (
    <button
      type="button"
      aria-current={isActive ? 'true' : undefined}
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm rounded-lg transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
        isActive ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-muted'
      )}
    >
      <span className={cn('w-2 h-2 rounded-full shrink-0', dotColor)} />
      <span className="truncate flex-1">
        {item.key.split('/').find((s) => s.startsWith('OPID_')) ?? item.key.split('/').pop() ?? item.key}
      </span>
    </button>
  );
}

export function SeriesPanel() {
  const { filtered, currentKey, jump } = useLabeling();
  const groups = buildGroups(filtered);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggleGroup = (toolId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(toolId)) next.delete(toolId);
      else next.add(toolId);
      return next;
    });
  };

  return (
    <div className="h-full overflow-hidden flex flex-col border-r border-border bg-card">
      <div className="flex-1 overflow-y-auto py-2">
        {groups.map((group) => {
          const isCollapsed = collapsed.has(group.toolId);
          return (
            <div key={group.toolId}>
              {groups.length > 1 && (
                <button
                  type="button"
                  aria-expanded={!isCollapsed}
                  onClick={() => toggleGroup(group.toolId)}
                  className="w-full flex items-center justify-between rounded-lg px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                  <span className="truncate">{group.toolId}</span>
                  <span className="flex-shrink-0 ml-1" aria-hidden>
                    {isCollapsed ? '›' : '‹'}
                  </span>
                </button>
              )}
              {!isCollapsed &&
                group.items.map((item) => (
                  <ItemRow
                    key={item.key}
                    item={item}
                    isActive={item.key === currentKey}
                    onClick={() => jump(item.key)}
                  />
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
